import { formatInZone, isThisYear } from "../../lib/time";

/**
 * Time and date wording for the diary, shared by the calendar, the Recent feed
 * and the meeting pane.
 *
 * This file used to hold the diary's list row too. The Today, Upcoming and
 * Recent lists it drew are now the calendar and the feed, and the row went
 * with them; the wording stayed, because all three still say times the same
 * way.
 */

/**
 * "09:00 – 10:00", or just "09:00" when nothing said how long it would run.
 *
 * An en dash with spaces around it, as a calendar writes it. A hyphen reads as
 * a join and a dash without spaces runs into the digits at this size.
 */
export function timeRange(startAt: string, endAt: string | null): string {
  const start = clock(startAt);
  const end = endAt ? clock(endAt) : "";
  return end ? `${start} – ${end}` : start;
}

/** "14:00". Twenty-four hour, because that is how a diary is read. */
export function clock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  return formatInZone(date, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * "Thursday, 13 August" -- the full form a calendar uses at the head of a day.
 *
 * Only for the detail page, which has one date on it and room to spell it out.
 * The lists use the short form, where forty of these would be a wall of words.
 */
export function longDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  return formatInZone(date, {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(isThisYear(date) ? {} : { year: "numeric" }),
  });
}

/** "Thu 13 Aug", or with the year once it stops being obvious. */
export function dayMonth(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  return formatInZone(date, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(isThisYear(date) ? {} : { year: "2-digit" }),
  });
}
